import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminSidebar from '../components/layout/AdminSidebar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Sparkles,
  FileText,
  Calendar,
  Clock,
  Eye,
  Edit2,
  Trash2,
  RefreshCw,
  Check,
  X,
  Upload,
  Image as ImageIcon,
  Video,
  Send,
  BarChart3,
  Lightbulb,
  Instagram,
  Facebook,
  Linkedin,
  Youtube,
  ChevronRight,
  Loader2,
  AlertCircle,
  CheckCircle,
  PenTool
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Status badge colors
const statusColors = {
  draft: 'bg-slate-100 text-slate-700',
  pending_review: 'bg-amber-100 text-amber-700',
  approved: 'bg-green-100 text-green-700',
  published: 'bg-blue-100 text-blue-700',
  rejected: 'bg-red-100 text-red-700'
};

const statusLabels = {
  draft: 'Draft',
  pending_review: 'Pending Review',
  approved: 'Approved',
  published: 'Published',
  rejected: 'Rejected'
};

// Content type icons
const contentTypeIcons = {
  blog: FileText,
  social: Send,
  video_script: Video
};

const AdminContentStudioPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [drafts, setDrafts] = useState([]);
  const [queue, setQueue] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [calendar, setCalendar] = useState({});
  const [selectedDraft, setSelectedDraft] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  
  // Generation form state
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [generateForm, setGenerateForm] = useState({
    content_type: 'social',
    topic_category: 'general',
    specific_topic: '',
    platform: 'all',
    tone: 'professional'
  });
  
  // Edit/Review state
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [regenerateDirection, setRegenerateDirection] = useState('');
  
  // Image upload
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    const isAdmin = localStorage.getItem('adminLoggedIn');
    if (!isAdmin) {
      navigate('/admin/login');
      return;
    }
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    try {
      const [statsRes, draftsRes, queueRes, suggestionsRes, calendarRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/content/stats`),
        axios.get(`${BACKEND_URL}/api/content/drafts`),
        axios.get(`${BACKEND_URL}/api/content/queue`),
        axios.get(`${BACKEND_URL}/api/content/topic-suggestions`),
        axios.get(`${BACKEND_URL}/api/content/calendar`)
      ]);
      
      setStats(statsRes.data);
      setDrafts(draftsRes.data);
      setQueue(queueRes.data);
      setSuggestions(suggestionsRes.data);
      setCalendar(calendarRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load content data');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    setIsGenerating(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/content/generate`, generateForm);
      toast.success('Content generated successfully!');
      setShowGenerateDialog(false);
      setGenerateForm({
        content_type: 'social',
        topic_category: 'general',
        specific_topic: '',
        platform: 'all',
        tone: 'professional'
      });
      fetchData();
      // Open the review dialog for the new content
      setSelectedDraft(response.data);
      setEditForm(response.data);
      setShowReviewDialog(true);
    } catch (error) {
      toast.error('Failed to generate content');
      console.error('Generation error:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleApprove = async (draftId) => {
    try {
      await axios.post(`${BACKEND_URL}/api/content/drafts/${draftId}/approve`);
      toast.success('Content approved!');
      setShowReviewDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to approve content');
    }
  };

  const handleReject = async (draftId, reason) => {
    try {
      await axios.post(`${BACKEND_URL}/api/content/drafts/${draftId}/reject?reason=${encodeURIComponent(reason || '')}`);
      toast.success('Content rejected');
      setShowReviewDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to reject content');
    }
  };

  const handleRegenerate = async (draftId) => {
    if (!regenerateDirection.trim()) {
      toast.error('Please provide direction for regeneration');
      return;
    }
    
    setIsRegenerating(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/content/drafts/${draftId}/regenerate`, {
        direction: regenerateDirection,
        keep_topic: true
      });
      toast.success('Content regenerated!');
      setSelectedDraft(response.data);
      setEditForm(response.data);
      setRegenerateDirection('');
    } catch (error) {
      toast.error('Failed to regenerate content');
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleUpdate = async (draftId) => {
    try {
      await axios.put(`${BACKEND_URL}/api/content/drafts/${draftId}`, editForm);
      toast.success('Content updated!');
      fetchData();
    } catch (error) {
      toast.error('Failed to update content');
    }
  };

  const handleDelete = async (draftId) => {
    if (!window.confirm('Are you sure you want to delete this content?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/content/drafts/${draftId}`);
      toast.success('Content deleted');
      setShowReviewDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete content');
    }
  };

  const handleImageUpload = async (e, draftId) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadingImage(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/content/drafts/${draftId}/upload-image`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success('Image uploaded!');
      setEditForm({ ...editForm, image_url: response.data.image_url });
      fetchData();
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const openReviewDialog = (draft) => {
    setSelectedDraft(draft);
    setEditForm(draft);
    setShowReviewDialog(true);
  };

  const generateFromSuggestion = (suggestion) => {
    setGenerateForm({
      ...generateForm,
      topic_category: suggestion.category,
      specific_topic: suggestion.topic,
      related_project_id: suggestion.related_type === 'project' ? suggestion.related_id : null,
      related_service_id: suggestion.related_type === 'service' ? suggestion.related_id : null
    });
    setShowGenerateDialog(true);
  };

  if (loading) {
    return (
      <AdminSidebar pageTitle="Content Studio" pageIcon={PenTool}>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
        </div>
      </AdminSidebar>
    );
  }

  return (
    <AdminSidebar pageTitle="Content Studio" pageIcon={PenTool}>
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.pending_review || 0}</p>
                <p className="text-sm text-slate-500">Pending Review</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Check className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.approved || 0}</p>
                <p className="text-sm text-slate-500">Approved</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Send className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.published || 0}</p>
                <p className="text-sm text-slate-500">Published</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-slate-100 rounded-lg">
                <FileText className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.total || 0}</p>
                <p className="text-sm text-slate-500">Total Content</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Sparkles className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.this_week || 0}</p>
                <p className="text-sm text-slate-500">This Week</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Tabs defaultValue="queue" className="space-y-4">
        <div className="flex items-center justify-between">
          <TabsList>
            <TabsTrigger value="queue" className="gap-2">
              <Clock className="w-4 h-4" />
              Review Queue
              {queue.count > 0 && (
                <Badge className="ml-1 bg-amber-500">{queue.count}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="all" className="gap-2">
              <FileText className="w-4 h-4" />
              All Content
            </TabsTrigger>
            <TabsTrigger value="calendar" className="gap-2">
              <Calendar className="w-4 h-4" />
              Calendar
            </TabsTrigger>
            <TabsTrigger value="ideas" className="gap-2">
              <Lightbulb className="w-4 h-4" />
              Topic Ideas
            </TabsTrigger>
          </TabsList>
          
          <Dialog open={showGenerateDialog} onOpenChange={setShowGenerateDialog}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-700 hover:to-cyan-700">
                <Sparkles className="w-4 h-4 mr-2" />
                Generate Content
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-purple-600" />
                  Generate AI Content
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Content Type</Label>
                  <Select
                    value={generateForm.content_type}
                    onValueChange={(v) => setGenerateForm({ ...generateForm, content_type: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="social">Social Media Post</SelectItem>
                      <SelectItem value="blog">Blog Article</SelectItem>
                      <SelectItem value="video_script">Video Script</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Topic Category</Label>
                  <Select
                    value={generateForm.topic_category}
                    onValueChange={(v) => setGenerateForm({ ...generateForm, topic_category: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">General</SelectItem>
                      <SelectItem value="services">Our Services</SelectItem>
                      <SelectItem value="projects">Project Highlights</SelectItem>
                      <SelectItem value="materials">Materials & Quality</SelectItem>
                      <SelectItem value="processes">Processes & How-To</SelectItem>
                      <SelectItem value="quality">Quality Standards</SelectItem>
                      <SelectItem value="customer_experience">Customer Experience</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Specific Topic (Optional)</Label>
                  <Textarea
                    value={generateForm.specific_topic}
                    onChange={(e) => setGenerateForm({ ...generateForm, specific_topic: e.target.value })}
                    placeholder="E.g., 'The importance of proper waterproofing in bathroom renovations'"
                    rows={2}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Platform</Label>
                  <Select
                    value={generateForm.platform}
                    onValueChange={(v) => setGenerateForm({ ...generateForm, platform: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Platforms</SelectItem>
                      <SelectItem value="facebook">Facebook</SelectItem>
                      <SelectItem value="instagram">Instagram</SelectItem>
                      <SelectItem value="linkedin">LinkedIn</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="outline">Cancel</Button>
                </DialogClose>
                <Button 
                  onClick={handleGenerate}
                  disabled={isGenerating}
                  className="bg-gradient-to-r from-purple-600 to-cyan-600"
                >
                  {isGenerating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Generate
                    </>
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Review Queue Tab */}
        <TabsContent value="queue" className="space-y-4">
          {queue.items?.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 mb-2">All caught up!</h3>
                <p className="text-slate-600 mb-4">No content pending review</p>
                <Button onClick={() => setShowGenerateDialog(true)} variant="outline">
                  <Sparkles className="w-4 h-4 mr-2" />
                  Generate New Content
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {queue.items?.map((item) => (
                <ContentCard 
                  key={item.id} 
                  item={item} 
                  onReview={() => openReviewDialog(item)}
                />
              ))}
            </div>
          )}
        </TabsContent>

        {/* All Content Tab */}
        <TabsContent value="all" className="space-y-4">
          {drafts.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 mb-2">No content yet</h3>
                <p className="text-slate-600 mb-4">Start by generating your first piece of content</p>
                <Button onClick={() => setShowGenerateDialog(true)}>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Generate Content
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {drafts.map((item) => (
                <ContentCard 
                  key={item.id} 
                  item={item} 
                  onReview={() => openReviewDialog(item)}
                />
              ))}
            </div>
          )}
        </TabsContent>

        {/* Calendar Tab */}
        <TabsContent value="calendar" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Content Calendar - {calendar.month}/{calendar.year}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {Object.keys(calendar.items || {}).length === 0 ? (
                <div className="text-center py-8">
                  <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-600">No scheduled content this month</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {Object.entries(calendar.items || {}).sort().map(([date, items]) => (
                    <div key={date} className="border-l-4 border-cyan-500 pl-4">
                      <h4 className="font-semibold text-slate-900 mb-2">
                        {new Date(date).toLocaleDateString('en-US', { 
                          weekday: 'long', 
                          month: 'short', 
                          day: 'numeric' 
                        })}
                      </h4>
                      <div className="space-y-2">
                        {items.map((item) => (
                          <div 
                            key={item.id}
                            className="flex items-center justify-between bg-slate-50 rounded-lg p-3"
                          >
                            <div className="flex items-center gap-3">
                              <Badge className={statusColors[item.status]}>
                                {statusLabels[item.status]}
                              </Badge>
                              <span className="font-medium text-slate-700">{item.title}</span>
                            </div>
                            <span className="text-sm text-slate-500">{item.scheduled_time}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Topic Ideas Tab */}
        <TabsContent value="ideas" className="space-y-4">
          <div className="bg-gradient-to-r from-purple-50 to-cyan-50 border border-purple-200 rounded-lg p-4 mb-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Lightbulb className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h3 className="font-semibold text-purple-900">Topic Suggestions</h3>
                <p className="text-sm text-purple-700 mt-1">
                  Click on any topic to generate content based on it. Topics are based on your services, projects, and industry expertise.
                </p>
              </div>
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-4">
            {suggestions.map((suggestion, idx) => (
              <Card 
                key={idx} 
                className="cursor-pointer hover:border-cyan-300 hover:shadow-md transition-all"
                onClick={() => generateFromSuggestion(suggestion)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <Badge variant="outline" className="mb-2">
                        {suggestion.category}
                      </Badge>
                      <h4 className="font-medium text-slate-900">{suggestion.topic}</h4>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-400" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Review/Edit Dialog */}
      <Dialog open={showReviewDialog} onOpenChange={setShowReviewDialog}>
        <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5" />
              Review Content
            </DialogTitle>
          </DialogHeader>
          
          {selectedDraft && (
            <div className="space-y-6 py-4">
              {/* Status & Meta */}
              <div className="flex items-center gap-4 flex-wrap">
                <Badge className={statusColors[selectedDraft.status]}>
                  {statusLabels[selectedDraft.status]}
                </Badge>
                <span className="text-sm text-slate-500">
                  {selectedDraft.content_type} • {selectedDraft.platform}
                </span>
                {selectedDraft.scheduled_date && (
                  <span className="text-sm text-slate-500">
                    Scheduled: {selectedDraft.scheduled_date} {selectedDraft.scheduled_time}
                  </span>
                )}
              </div>
              
              {/* Headline */}
              <div className="space-y-2">
                <Label>Headline / Title</Label>
                <Input
                  value={editForm.headline || ''}
                  onChange={(e) => setEditForm({ ...editForm, headline: e.target.value })}
                />
              </div>
              
              {/* Body */}
              <div className="space-y-2">
                <Label>Content Body</Label>
                <Textarea
                  value={editForm.body || ''}
                  onChange={(e) => setEditForm({ ...editForm, body: e.target.value })}
                  rows={8}
                  className="font-mono text-sm"
                />
              </div>
              
              {/* Excerpt (for social) */}
              {selectedDraft.content_type === 'social' && (
                <div className="space-y-2">
                  <Label>Extended Version (LinkedIn)</Label>
                  <Textarea
                    value={editForm.excerpt || ''}
                    onChange={(e) => setEditForm({ ...editForm, excerpt: e.target.value })}
                    rows={3}
                  />
                </div>
              )}
              
              {/* Hashtags */}
              <div className="space-y-2">
                <Label>Hashtags</Label>
                <Input
                  value={(editForm.hashtags || []).join(', ')}
                  onChange={(e) => setEditForm({ 
                    ...editForm, 
                    hashtags: e.target.value.split(',').map(t => t.trim()).filter(t => t)
                  })}
                  placeholder="#construction, #homeimprovement"
                />
              </div>
              
              {/* Call to Action */}
              <div className="space-y-2">
                <Label>Call to Action</Label>
                <Input
                  value={editForm.call_to_action || ''}
                  onChange={(e) => setEditForm({ ...editForm, call_to_action: e.target.value })}
                />
              </div>
              
              {/* Image */}
              <div className="space-y-2">
                <Label>Image</Label>
                <div className="flex items-start gap-4">
                  {editForm.image_url ? (
                    <div className="relative w-40 h-40 rounded-lg overflow-hidden bg-slate-100">
                      <img 
                        src={editForm.image_url.startsWith('/api') ? `${BACKEND_URL}${editForm.image_url}` : editForm.image_url}
                        alt="Content" 
                        className="w-full h-full object-cover"
                      />
                      <button
                        onClick={() => setEditForm({ ...editForm, image_url: null })}
                        className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="w-40 h-40 border-2 border-dashed border-slate-300 rounded-lg flex items-center justify-center">
                      <ImageIcon className="w-8 h-8 text-slate-400" />
                    </div>
                  )}
                  <div className="flex-1">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e, selectedDraft.id)}
                      className="hidden"
                      id="image-upload"
                    />
                    <label htmlFor="image-upload">
                      <Button variant="outline" disabled={uploadingImage} asChild>
                        <span>
                          {uploadingImage ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Upload className="w-4 h-4 mr-2" />
                          )}
                          Upload Image
                        </span>
                      </Button>
                    </label>
                    <p className="text-xs text-slate-500 mt-2">
                      Upload a high-quality image from your portfolio or project photos
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Regeneration Direction */}
              <div className="space-y-2 p-4 bg-purple-50 rounded-lg">
                <Label className="text-purple-900">AI Regeneration</Label>
                <Textarea
                  value={regenerateDirection}
                  onChange={(e) => setRegenerateDirection(e.target.value)}
                  placeholder="Provide direction for AI to improve this content (e.g., 'Make it more technical', 'Focus on customer benefits', 'Add more specific examples')"
                  rows={2}
                />
                <Button 
                  variant="outline" 
                  onClick={() => handleRegenerate(selectedDraft.id)}
                  disabled={isRegenerating || !regenerateDirection.trim()}
                  className="border-purple-300 text-purple-700 hover:bg-purple-100"
                >
                  {isRegenerating ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4 mr-2" />
                  )}
                  Regenerate with Direction
                </Button>
              </div>
              
              {/* Revision History */}
              {selectedDraft.revision_count > 0 && (
                <div className="text-sm text-slate-500">
                  <span className="font-medium">Revisions:</span> {selectedDraft.revision_count}
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <div className="flex gap-2 flex-1">
              <Button 
                variant="outline" 
                onClick={() => handleDelete(selectedDraft?.id)}
                className="text-red-600 hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </Button>
              <Button 
                variant="outline" 
                onClick={() => handleReject(selectedDraft?.id, '')}
              >
                <X className="w-4 h-4 mr-2" />
                Reject
              </Button>
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={() => handleUpdate(selectedDraft?.id)}
              >
                <Edit2 className="w-4 h-4 mr-2" />
                Save Changes
              </Button>
              <Button 
                onClick={() => handleApprove(selectedDraft?.id)}
                className="bg-green-600 hover:bg-green-700"
              >
                <Check className="w-4 h-4 mr-2" />
                Approve
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminSidebar>
  );
};

// Content Card Component
const ContentCard = ({ item, onReview }) => {
  const TypeIcon = contentTypeIcons[item.content_type] || FileText;
  
  return (
    <Card className="hover:border-cyan-300 transition-colors">
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          {/* Image Preview */}
          <div className="w-20 h-20 rounded-lg bg-slate-100 flex-shrink-0 overflow-hidden">
            {item.image_url ? (
              <img 
                src={item.image_url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${item.image_url}` : item.image_url}
                alt="" 
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <TypeIcon className="w-8 h-8 text-slate-400" />
              </div>
            )}
          </div>
          
          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Badge className={statusColors[item.status]}>
                {statusLabels[item.status]}
              </Badge>
              <Badge variant="outline">{item.content_type}</Badge>
            </div>
            
            <h3 className="font-semibold text-slate-900 truncate">{item.headline || item.title}</h3>
            <p className="text-sm text-slate-600 line-clamp-2 mt-1">
              {item.body?.substring(0, 150)}...
            </p>
            
            <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
              {item.scheduled_date && (
                <span className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {item.scheduled_date}
                </span>
              )}
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {new Date(item.created_at).toLocaleDateString()}
              </span>
              {/* Platform icons */}
              <div className="flex items-center gap-1">
                {(item.platform === 'all' || item.platform === 'facebook') && (
                  <Facebook className="w-3 h-3 text-blue-600" />
                )}
                {(item.platform === 'all' || item.platform === 'instagram') && (
                  <Instagram className="w-3 h-3 text-pink-600" />
                )}
                {(item.platform === 'all' || item.platform === 'linkedin') && (
                  <Linkedin className="w-3 h-3 text-blue-700" />
                )}
              </div>
            </div>
          </div>
          
          {/* Actions */}
          <Button variant="outline" size="sm" onClick={onReview}>
            <Eye className="w-4 h-4 mr-2" />
            Review
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default AdminContentStudioPage;
